const AbstractRepository = require('@/lib/Repositories/AbstractRepository');
const { createModelBasicHydrator } = require('@/lib/Hydration/basicHydrator');

/**
 * @abstract
 */
class AbstractItemRepository extends AbstractRepository {
    /**
     * @param {DataStore} dataStore
     * @param {Hydrator} hydrator
     * @param {SyncStateManager} syncStateManager
     * @param {ItemBarcodeRepository} itemBarcodeRepository
     */
    constructor(
        dataStore,
        hydrator,
        syncStateManager,
        itemBarcodeRepository
    ) {
        super(dataStore, hydrator, syncStateManager);

        this.itemBarcodeRepository = itemBarcodeRepository;
    }

    clearDataBeforeInserting() {
        return false;
    }

    async persist(model) {
        await super.persist(model);

        await this.itemBarcodeRepository.replaceForItem(model);
    }

    async persistMany(models) {
        await super.persistMany(models);

        await this.itemBarcodeRepository.replaceForItems(models);
    }

    /**
     * Returns all active items in the given category.
     *
     * @param {number} catID
     * @return {Promise<AbstractItem[]>}
     */
    findByCategory(catID) {
        return this.dataStore
            .getStore(this.getTableName())
            .where({ catID })
            .and((item) => item.status === 'Active')
            .toArray()
            .then(createModelBasicHydrator(this.getEntityClass()));
    }

    /**
     * @protected
     *
     * Receives an array of raw data from the API and returns an array of hydrated models
     * of the appropriate type for this repository.
     *
     * @param {{}[]} rawData
     * @param {number|null} [branchID] - The branchID the data was requested for, where applicable.
     *
     * @return {Array}
     */
    hydrateModelsFromApi(rawData, branchID) {
        return this.hydrator.hydrateArray(
            rawData,
            () => {
                const model = new (this.getEntityClass())();

                if (branchID) {
                    // The API doesn't reliably return the branchID.
                    // We know we asked for items for this branch, so set it on the model here.
                    model.branchID = branchID;
                }

                return model;
            }
        );
    }

    sanitizeDataFromApi(rawData) {
        if (rawData) {
            rawData.forEach((data) => {
                // Do some processing of the data before giving it to the hydrator...
                // Rename `price` to `onlinePrice`:
                data.onlinePrice = data.price;
                delete data.price;
            });
        }

        return rawData;
    }

    findActiveForBranch(branchID) {
        return this.dataStore
            .getStore(this.getTableName())
            // branchIDs is a MultiEntry index:
            // https://github.com/dexie/Dexie.js/issues/871#issuecomment-513874791
            // https://dexie.org/docs/MultiEntry-Index
            .where({ branchIDs: branchID, status: 'Active' })
            .toArray()
            .then(createModelBasicHydrator(this.getEntityClass()));
    }
}

module.exports = AbstractItemRepository;
